// You can create a new file e.g., /utils/formatDate.ts
import { format, isToday, isYesterday, isSameDay, differenceInMinutes } from 'date-fns';
import { Message } from '../hooks/useMessages';

/**
 * Formats a date string for display as a timestamp separator in the chat.
 * - "Today"
 * - "Yesterday"
 * - "Wednesday" (for a day in the last week)
 * - "Jul 31, 2025" (for older dates)
 */
export const formatTimestampHeader = (dateStr: string): string => {
  const date = new Date(dateStr);
  if (isToday(date)) {
    return 'Today';
  }
  if (isYesterday(date)) {
    return 'Yesterday';
  }
  // For a full date like "Wednesday, July 31, 2025"
  return format(date, 'PPPP'); 
  // Or for just the day name like "Wednesday"
  // return format(date, 'eeee'); 
  // Or for a shorter date like "Jul 31, 2025"
  // return format(date, 'MMM d, yyyy');
};

export const formatTimestamp = (currentMessage: Message, prevMessage: Message | null): string => {
    const currentDate = new Date(currentMessage.created_at);
    const prevDate = prevMessage ? new Date(prevMessage.created_at) : null;
  
    // Check if the reason for showing is a new day
    const isNewDay = !prevDate || !isSameDay(currentDate, prevDate);
  
    if (isNewDay) {
      if (isToday(currentDate)) return 'Today';
      if (isYesterday(currentDate)) return 'Yesterday';
      return format(currentDate, 'PPPP'); // e.g., "Thursday, July 31, 2025"
    } else {
      // If it's not a new day, it must be a time gap. Just show the time.
      return format(currentDate, 'p'); // e.g., "10:19 AM"
    }
  };

/**
 * Determines if a timestamp separator should be shown between two messages.
 */
export const shouldShowTimestamp = (prevMessage: Message | null, currentMessage: Message): boolean => {
    // 1. Always show for the very first message
    if (!prevMessage) {
      return true;
    }
  
    const prevDate = new Date(prevMessage.created_at);
    const currentDate = new Date(currentMessage.created_at);
  
    // 2. Show if the day is different
    if (!isSameDay(prevDate, currentDate)) {
      return true;
    }
  
    // 3. Show if it's the same day, but more than 30 minutes have passed
    if (differenceInMinutes(currentDate, prevDate) > 30) {
      return true;
    }
  
    // Otherwise, hide it
    return false;
  };


  // src/utils/formatDate.ts

export const formatRelativeTime = (isoDateString: string | null): string => {
    if (!isoDateString) return '';
  
    const now = new Date();
    const messageDate = new Date(isoDateString);
    const diffInSeconds = Math.floor((now.getTime() - messageDate.getTime()) / 1000);
  
    if (diffInSeconds < 60) {
        return 'Just now';
    }
  
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m`;
    }
  
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours}h`;
    }
  
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
      return `${diffInDays}d`;
    }
  
    const diffInWeeks = Math.floor(diffInDays / 7);
    return `${diffInWeeks}w`;
  };